'use client'

import { cn } from '@/lib/utils'
import { useEffect, useRef } from 'react'

const RETRY_EVENTS = ['pointerdown', 'touchstart', 'scroll', 'keydown'] as const

/**
 * Silent, control-less looping video. The `autoplay` attribute alone isn't enough: iOS Low Power
 * Mode and data-saver block it and Safari paints a play button instead. So we start playback from
 * code and, if the browser refuses, retry on the visitor's first interaction (which unlocks media).
 * Off-screen videos are paused so a gallery of many clips only decodes the ones in view.
 */
export function AutoplayVideo({ src, className, label }: { src: string; className?: string; label?: string }) {
  const ref = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = ref.current
    if (!video) return
    video.muted = true // browsers check the property, not the attribute, when allowing autoplay

    let visible = true
    const play = () => {
      if (!visible) return
      video.play().then(removeRetry, () => {})
    }
    const removeRetry = () => RETRY_EVENTS.forEach((e) => window.removeEventListener(e, play))

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting
        if (visible) play()
        else video.pause()
      },
      { rootMargin: '200px 0px' },
    )
    observer.observe(video)

    RETRY_EVENTS.forEach((e) => window.addEventListener(e, play, { passive: true }))
    play()
    return () => {
      observer.disconnect()
      removeRetry()
    }
  }, [src])

  return (
    <video
      ref={ref}
      src={src}
      className={cn('autoplay-video', className)}
      autoPlay
      muted
      loop
      playsInline
      disablePictureInPicture
      disableRemotePlayback
      preload="auto"
      aria-label={label}
    />
  )
}
